<template>
    <div class="field-selector">
        <!-- Primitive Fields -->
        <div class="field-group primitive-fields">
            <div class="group-title">{{ $t('table.primitiveFields') }}</div>
            <div class="group-content">
                <div class="field-item" @click="selectField('text')" v-tippy="{ content: $t('table.fsTooltipText') }">
                    <i class="material-icons">text_fields</i>
                    <span>{{ $t("tables.text") }}</span>
                </div>
                <div class="field-item" @click="selectField('long-text')" v-tippy="{ content: $t('table.fsTooltipLongText') }">
                    <i class="material-icons">subject</i>
                    <span>{{ $t("tables.longText") }}</span>
                </div>
                <div class="field-item" @click="selectField('number')" v-tippy="{ content: $t('table.fsTooltipNumber') }">
                    <i class="material-icons">looks_one</i>
                    <span>{{ $t("tables.number") }}</span>
                </div>
                <div class="field-item" @click="selectField('currency')" v-tippy="{ content: $t('table.fsTooltipCurrency') }">
                    <i class="material-icons">attach_money</i>
                    <span>{{ $t("tables.currency") }}</span>
                </div>
                <div class="field-item" @click="selectField('datetime')" v-tippy="{ content: $t('table.fsTooltipDatetime') }">
                    <i class="material-icons">event</i>
                    <span>{{ $t("tables.datetime") }}</span>
                </div>
                <div class="field-item" @click="selectField('dropdown')" v-tippy="{ content: $t('table.fsTooltipDropdown') }">
                    <i class="material-icons">arrow_drop_down</i>
                    <span>{{ $t("tables.dropdown") }}</span>
                </div>
                <div class="field-item" @click="selectField('checkbox')" v-tippy="{ content: $t('table.fsTooltipCheckbox') }">
                    <i class="material-icons">check_box</i>
                    <span>{{ $t("tables.checkbox") }}</span>
                </div>
            </div>
        </div>

        <!-- Validator Fields -->
        <div class="field-group validator-fields">
            <div class="group-title">{{ $t('table.validatorFields') }}</div>
            <div class="group-content">
                <div class="field-item" @click="selectField('url')" v-tippy="{ content: $t('table.fsTooltipUrl') }">
                    <i class="material-icons">link</i>
                    <span>{{ $t("table.fsLabelUrl") }}</span>
                </div>
                <div class="field-item" @click="selectField('email')" v-tippy="{ content: $t('table.fsTooltipEmail') }">
                    <i class="material-icons">email</i>
                    <span>{{ $t("tables.email") }}</span>
                </div>
                <div class="field-item" @click="selectField('phone')" v-tippy="{ content: $t('table.fsTooltipPhone') }">
                    <i class="material-icons">phone</i>
                    <span>{{ $t("tables.phone") }}</span>
                </div>
            </div>
        </div>

        <!-- Module Fields -->
        <div class="field-group module-fields">
            <div class="group-title">{{ $t('table.moduleFields') }}</div>
            <div class="group-content">
                <div class="field-item" @click="selectField('contact')" v-tippy="{ content: $t('table.fsTooltipContact') }">
                    <i class="material-icons">person</i>
                    <span>{{ $t("modules.contact") }}</span>
                </div>
                <div class="field-item not-implemented" @click="selectField('ticket')" v-tippy="{ content: $t('table.fsTooltipTicket') }">
                    <i class="material-icons">confirmation_number</i>
                    <span>{{ $t("message.ticket") }}</span>
                </div>
                <div class="field-item" @click="selectField('task')" v-tippy="{ content: $t('table.fsTooltipTask') }">
                    <i class="material-icons">assignment</i>
                    <span>{{ $t("message.task") }}</span>
                </div>
                <div class="field-item not-implemented" @click="selectField('event')" v-tippy="{ content: $t('table.fsTooltipEvent') }">
                    <i class="material-icons">event</i>
                    <span>{{ $t("message.event") }}</span>
                </div>
                <div class="field-item" @click="selectField('user')" v-tippy="{ content: $t('table.fsTooltipUser') }">
                    <i class="material-icons">support_agent</i>
                    <span>{{ $t("message.user") }}</span>
                </div>
                <div class="field-item" @click="selectField('conversation')" v-tippy="{ content: $t('table.fsTooltipConversation') }">
                    <i class="material-icons">message</i>
                    <span>{{ $t("tables.linkedFieldConversation") }}</span>
                </div>
            </div>
        </div>

        <!-- Record Metadata Fields -->
        <div class="field-group metadata-fields">
            <div class="group-title">{{ $t('table.metaFields') }}</div>
            <div class="group-content">
                <div class="field-item" @click="selectField('record_id')" v-tippy="{ content: $t('table.metaFieldRecordIdTooltip') }">
                    <i class="material-icons">tag</i>
                    <span>{{ $t('table.metaFieldRecordId') }}</span>
                </div>
                <div class="field-item" @click="selectField('record_created_at')" v-tippy="{ content: $t('table.metaFieldRecordCreatedAtTooltip') }">
                    <i class="material-icons">add_circle_outline</i>
                    <span>{{ $t('table.metaFieldRecordCreatedAt') }}</span>
                </div>
                <div class="field-item" @click="selectField('record_updated_at')" v-tippy="{ content: $t('table.metaFieldRecordUpdatedAtTooltip') }">
                    <i class="material-icons">update</i>
                    <span>{{ $t('table.metaFieldRecordUpdatedAt') }}</span>
                </div>
                <div class="field-item" @click="selectField('record_created_by')" v-tippy="{ content: $t('table.metaFieldRecordCreatedByTooltip') }">
                    <i class="material-icons">person_add</i>
                    <span>{{ $t('table.metaFieldRecordCreatedBy') }}</span>
                </div>
                <div class="field-item" @click="selectField('record_updated_by')" v-tippy="{ content: $t('table.metaFieldRecordUpdatedByTooltip') }">
                    <i class="material-icons">person_outline</i>
                    <span>{{ $t('table.metaFieldRecordUpdatedBy') }}</span>
                </div>
            </div>
        </div>

        <!-- File Fields -->
        <div class="field-group file-fields">
            <div class="group-title">{{ $t('table.fileFields') }}</div>
            <div class="group-content">
                <div class="field-item" @click="selectField('file')" v-tippy="{ content: $t('table.fsTooltipFile') }">
                    <i class="material-icons">attachment</i>
                    <span>{{ $t("tables.file") }}</span>
                </div>
                <div class="field-item" @click="selectField('image')" v-tippy="{ content: $t('table.fsTooltipImage') }">
                    <i class="material-icons">image</i>
                    <span>{{ $t("tables.image") }}</span>
                </div>
                <div class="field-item" @click="selectField('video')" v-tippy="{ content: $t('table.fsTooltipVideo') }">
                    <i class="material-icons">videocam</i>
                    <span>{{ $t("tables.video") }}</span>
                </div>
                <div class="field-item" @click="selectField('audio')" v-tippy="{ content: $t('table.fsTooltipAudio') }">
                    <i class="material-icons">audiotrack</i>
                    <span>{{ $t("tables.audio") }}</span>
                </div>
                <div class="field-item" @click="selectField('multi-file')" v-tippy="{ content: $t('table.fsTooltipMultiFile') }">
                    <i class="material-icons">folder</i>
                    <span>{{ $t("tables.multiFile") }}</span>
                </div>
                <div class="field-item" @click="selectField('multi-image')" v-tippy="{ content: $t('table.fsTooltipMultiImage') }">
                    <i class="material-icons">photo_library</i>
                    <span>{{ $t("tables.multiImage") }}</span>
                </div>
                <div class="field-item" @click="selectField('multi-video')" v-tippy="{ content: $t('table.fsTooltipMultiVideo') }">
                    <i class="material-icons">video_library</i>
                    <span>{{ $t("tables.multiVideo") }}</span>
                </div>
                <div class="field-item" @click="selectField('multi-audio')" v-tippy="{ content: $t('table.fsTooltipMultiAudio') }">
                    <i class="material-icons">library_music</i>
                    <span>{{ $t("tables.multiAudio") }}</span>
                </div>
            </div>
        </div>

        <!-- Special Fields -->
        <div class="field-group special-fields">
            <div class="group-title">{{ $t('table.specialFields') }}</div>
            <div class="group-content">
                <div class="field-item" @click="selectField('ai')" v-tippy="{ content: $t('table.fsTooltipAi') }">
                    <i class="material-icons">auto_awesome</i>
                    <span>{{ $t("table.fsLabelAiField") }}</span>
                </div>

                <div class="field-item" @click="selectField('formula')" v-tippy="{ content: $t('table.fsTooltipFormula') }">
                    <i class="material-icons">functions</i>
                    <span>{{ $t("table.fsLabelFormula") }}</span>
                </div>

                <div class="field-item" @click="selectField('lookup')" v-tippy="{ content: $t('table.fsTooltipLookup') }">
                    <i class="material-icons">sync_alt</i>
                    <span>{{ $t("table.fsLabelLookup") }}</span>
                </div>

                <div class="field-item" @click="selectField('auto-increment')" v-tippy="{ content: $t('table.fsTooltipAutoIncrement') }">
                    <i class="material-icons">plus_one</i>
                    <span>{{ $t("table.fsLabelAutoIncrement") }}</span>
                </div>

                <PermissionController :permissions="['automation.get', 'table.update']" controller="every">
                    <div class="field-item" @click="createAutomationButton">
                        <i class="material-icons">smart_button</i>
                        <span>{{ $t("tables.automationButton") }}</span>
                    </div>
                </PermissionController>

                <div class="field-item" @click="selectField('linked-record')" v-tippy="{ content: $t('table.fsTooltipLinkedRecord') }">
                    <i class="material-icons">switch_access_shortcut</i>
                    <span>{{ $t("table.fsLabelLinkedRecord") }}</span>
                </div>
                <div class="field-item" @click="selectField('json')" v-tippy="{ content: $t('table.fsTooltipJson') }">
                    <i class="material-icons">code</i>
                    <span>{{ $t("table.fsLabelJson") }}</span>
                </div>
                <div class="field-item" @click="selectField('color')" v-tippy="{ content: $t('table.fsTooltipColor') }">
                    <i class="material-icons">palette</i>
                    <span>{{ $t("tables.color") }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";

export default Vue.extend({
    name: "FieldSelector",
    methods: {
        selectField(fieldType) {
            if (["ticket"].includes(fieldType)) {
                this.$message.error("This feature is not available yet.");
                return;
            }
            this.$emit("field-selected", fieldType);
        },
        createAutomationButton() {
            this.$emit("close");
            this.$feature.beforeCreateButtonAutomation(() => {
                this.selectField("automation-button");
            });
        },
    },
});
</script>

<style scoped lang="scss">
.field-selector {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: stretch;

    .field-group {
        flex: 1;
        margin: 0;
        display: flex;
        flex-direction: column;
        border-right: 2px dashed #ccc;

        &:last-child {
            border-right: none;
        }

        .group-title {
            display: flex;
            align-items: center;
            background-color: #f0f0f5;
            padding: 8px;
            margin-bottom: 8px;
            font-weight: bold;
            border-radius: 4px;
            color: #333;
        }

        .group-content {
            display: flex;
            flex-direction: column;
            flex-grow: 1;

            .field-item {
                display: flex;
                align-items: center;
                padding: 6px;
                cursor: pointer;
                border-radius: 4px;
                margin-bottom: 4px;
                border-bottom: 1px solid #ddd;
                white-space: nowrap;
                min-width: 165px;

                &:hover {
                    background-color: #eef1f6;
                }

                i {
                    margin-right: 6px;
                }

                &.not-implemented {
                    display: none;
                }
            }
        }
    }

    /* Örnek renk uyumu: Gruplara farklı pastel tonlar ekleyebilirsiniz */
    .primitive-fields {
        background-color: #fafaff;
    }

    .validator-fields {
        background-color: #fffafa;
    }

    .module-fields {
        background-color: #fafffa;
    }

    .file-fields {
        background-color: #fefaf0;
    }

    .special-fields {
        background-color: #f9f0ff;
    }
    .metadata-fields {
        background-color: #f0faff;
    }
}
</style>
